import { ResponseGroupMetaVariableType, ResponseGroupType, ResponseType, SubResponseType } from "@/common/interfaces";
import mitt, { Emitter, EventType } from 'mitt';
import moment from "moment";
import { Moment } from "moment";

export declare type ResponseDict = {[questionUuid: string]: Response};

export declare type ChoiceResponse = {
	choiceUuid: string;
	text?: string | null;
	rank?: number | null;
};

export function convertRemoteResponses(remoteResponses: Array<ResponseType>): ResponseDict {
	const result: ResponseDict = {};

	for (const remoteResponse of remoteResponses) {
		result[remoteResponse.questionUuid] = Response.fromRemote(remoteResponse);
	}

	return result;
}

export class Response {
	questionUuid: string;
	text: string | null; // Short Text, Long Text
	value: number | null; // NPS, Star, Scale
	choices: Array<ChoiceResponse> | null; // Dropdown, single choice, checkbox
	subResponses: Array<SubResponseType> | null; // Matrix questions or questions that use SubQuestion &| SubQuestionEntry

	constructor(
		questionUuid: string,
		text: string | null,
		value: number | null,
		choices: Array<ChoiceResponse> | null,
		subResponses: Array<SubResponseType> | null
	) {
		this.questionUuid = questionUuid;
		this.text = text;
		this.value = value;
		this.choices = choices;
		this.subResponses = subResponses;
	}

	updateFromRemote(remoteResponse: ResponseType): void {
		console.assert(remoteResponse.questionUuid === this.questionUuid, "Mismatch on update to response");
		this.text = remoteResponse.text;
		this.value = remoteResponse.value;
		this.choices = remoteResponse.choices;
		this.subResponses = remoteResponse.subResponses;
	}

	static fromRemote(remoteResponse: ResponseType): Response {
		return new Response(
			remoteResponse.questionUuid,
			remoteResponse.text,
			remoteResponse.value,
			remoteResponse.choices,
			remoteResponse.subResponses
		);
	}
}

export class ResponseGroup {
	uuid: string;
	surveyUuid: string;
	publishedSurveyUuid: string;
	createdAt: Moment;
	updatedAt: Moment;
	responses: ResponseDict;
	metaVariables: Array<ResponseGroupMetaVariableType>;

	events: Emitter<Record<EventType, unknown>>;

	constructor(
		uuid: string,
		surveyUuid: string,
		publishedSurveyUuid: string,
		responses: ResponseDict,
		createdAt: Moment,
		updatedAt: Moment,
		metaVariables: Array<ResponseGroupMetaVariableType>
	) {
		this.events = mitt();

		this.uuid = uuid;
		this.surveyUuid = surveyUuid;
		this.publishedSurveyUuid = publishedSurveyUuid;
		this.responses = {...responses};
		this.createdAt = createdAt;
		this.updatedAt = updatedAt;
		this.metaVariables = [...metaVariables];
	}

	setMetaVariable(variableUuid: string, value: string | number | null): void {
		const metaVariable = this.metaVariables.find((mv: ResponseGroupMetaVariableType) => mv.variableUuid === variableUuid);

		if (metaVariable) {
			if (typeof value === "string") {
				metaVariable.charValue = value;
			} else if (typeof value === "number") {
				metaVariable.intValue = value;
			} else {
				metaVariable.charValue = null;
				metaVariable.intValue = null;
			}
		}
	}

	getMetaVariable(variableUuid: string): string | number | null {
		const metaVariable = this.metaVariables.find((mv: ResponseGroupMetaVariableType) => mv.variableUuid === variableUuid);

		if (metaVariable) {
			if (metaVariable.charValue !== null) {
				return metaVariable.charValue;
			} else if (metaVariable.intValue !== null) {
				return metaVariable.intValue;
			}
		}

		return null;
	}

	getResponse(questionUuid: string): Response | null {
		if (this.responses[questionUuid] === undefined) {
			return null;
		}

		return this.responses[questionUuid];
	}

	setResponse(
		questionUuid: string,
		text: string | null,
		value: number | null,
		choices: Array<ChoiceResponse> | null,
		subResponses: Array<SubResponseType> | null
	): void {
		if (this.responses[questionUuid] === undefined) {
			this.responses[questionUuid] = new Response(
				questionUuid, text, value, choices, subResponses
			);
			this.events.emit('response:updated', questionUuid);
			return;
		}
		this.responses[questionUuid].text = text;
		this.responses[questionUuid].value = value;
		this.responses[questionUuid].choices = (choices)? [...choices] : null;
		this.responses[questionUuid].subResponses = (subResponses)? [...subResponses] : null;

		this.events.emit('response:updated', questionUuid);
	}

	clearResponse(
		questionUuid: string
	): void {
		if (this.responses[questionUuid] !== undefined) {
			delete this.responses[questionUuid];
			this.events.emit('response:updated', questionUuid);
		}
	}

	updateFromRemote(remoteResponseGroup: ResponseGroupType): void {
		console.assert(
			(
				remoteResponseGroup.uuid === this.uuid
				&& remoteResponseGroup.surveyUuid === this.surveyUuid
			),
			"Attempting to update a responsegroup with an entirely different one."
		);

		this.publishedSurveyUuid = remoteResponseGroup.publishedSurveyUuid;

		// We treat the remote as the source of truth for responses
		this.responses = convertRemoteResponses(remoteResponseGroup.responses);

		this.updatedAt = moment(remoteResponseGroup.updatedAt);

		this.events.emit('responsegroup:updated');
	}

	static fromRemote(remoteResponseGroup: ResponseGroupType): ResponseGroup {
		return new ResponseGroup(
			remoteResponseGroup.uuid,
			remoteResponseGroup.surveyUuid,
			remoteResponseGroup.publishedSurveyUuid,
			convertRemoteResponses(remoteResponseGroup.responses),
			moment(remoteResponseGroup.createdAt),
			moment(remoteResponseGroup.updatedAt),
			remoteResponseGroup.metaVariables
		);
	}
}
